import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 * @property {HTMLScriptElement} [variantPriceMap]
 */

/**
 * @typedef {{ price: string, compare: string, show_compare: boolean }} OborVariantPrice
 */

/**
 * Product price — updates from Liquid variant price map on variant change
 * (avoids BSS blanking Horizon morph HTML).
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  /** @type {number[]} */
  #protectTimers = [];

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearProtectTimers();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  #clearProtectTimers() {
    for (const id of this.#protectTimers) clearTimeout(id);
    this.#protectTimers = [];
  }

  /**
   * @returns {Record<string, OborVariantPrice> | null}
   */
  #readPriceMap() {
    const el =
      this.querySelector('[ref="variantPriceMap"]') ||
      this.querySelector('script[type="application/json"]') ||
      this.refs.variantPriceMap;
    if (!(el instanceof HTMLScriptElement)) return null;
    try {
      return JSON.parse(el.textContent || '{}');
    } catch {
      return null;
    }
  }

  /**
   * Apply prices from the Liquid-built map (dealer-aware).
   * @param {string|number} variantId
   * @returns {boolean}
   */
  #applyFromPriceMap(variantId) {
    if (variantId == null || variantId === '') return false;
    const map = this.#readPriceMap();
    if (!map) return false;
    const entry = map[String(variantId)];
    if (!entry?.price) return false;

    let container =
      this.querySelector(':scope > [ref="priceContainer"]') || this.querySelector('[ref="priceContainer"]');

    if (!(container instanceof HTMLElement)) {
      container = document.createElement('div');
      container.setAttribute('ref', 'priceContainer');
      const anchor = this.querySelector('.tax-note, .installments');
      if (anchor) anchor.insertAdjacentElement('beforebegin', container);
      else this.append(container);
    }

    const saleFirst = this.dataset.showSaleFirst === 'true';
    const showCompare = Boolean(entry.show_compare);
    const price = entry.price;
    const compare = entry.compare || '';

    if (showCompare) {
      const saleHtml = `<span class="price-item__group"><span class="visually-hidden">Sale price&nbsp;</span><span class="price-item--sale price">${price}</span></span>`;
      const compareHtml = `<span class="price-item__group"><span class="visually-hidden">Regular price&nbsp;</span><span class="price-item--regular compare-at-price">${compare}</span></span>`;
      container.innerHTML = `
        <div class="price__regular price__hidden"><span class="price">${price}</span></div>
        <div class="price__sale">${saleFirst ? saleHtml + compareHtml : compareHtml + saleHtml}</div>
      `;
    } else {
      container.innerHTML = `
        <div class="price__regular"><span class="price">${price}</span></div>
        <div class="price__sale price__hidden"></div>
      `;
    }

    return true;
  }

  /**
   * @param {string} htmlSnapshot
   */
  #protectPriceFromBlank(htmlSnapshot) {
    this.#clearProtectTimers();
    if (!htmlSnapshot) return;

    const restoreIfBlank = () => {
      const live =
        this.querySelector(':scope > [ref="priceContainer"]') || this.querySelector('[ref="priceContainer"]');
      if (!(live instanceof HTMLElement)) return;
      const text = (live.textContent || '').replace(/\s+/g, ' ').trim();
      if (!text || !/\d/.test(text)) {
        live.innerHTML = htmlSnapshot;
      }
    };

    requestAnimationFrame(restoreIfBlank);
    for (const ms of [50, 150, 400, 800]) {
      this.#protectTimers.push(window.setTimeout(restoreIfBlank, ms));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const variantId = event.detail.resource?.id;
    const newProductPrice = event.detail.data.html?.querySelector?.(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );

    // Keep price map in sync when section HTML includes a fresh copy (combined listings)
    if (newProductPrice) {
      const newMap = newProductPrice.querySelector('[ref="variantPriceMap"]');
      const ourMap = this.querySelector('[ref="variantPriceMap"]');
      if (newMap && ourMap) ourMap.textContent = newMap.textContent;
    }

    // Primary: client map built in Liquid (includes dealer Drop Price, no BSS hooks)
    if (variantId != null && this.#applyFromPriceMap(variantId)) {
      const live =
        this.querySelector(':scope > [ref="priceContainer"]') || this.querySelector('[ref="priceContainer"]');
      if (live instanceof HTMLElement) {
        this.#protectPriceFromBlank(live.innerHTML);
      }
    } else if (newProductPrice) {
      const priceContainer =
        this.querySelector(':scope > [ref="priceContainer"]') ||
        this.querySelector('[ref="priceContainer"]') ||
        this.refs.priceContainer;

      const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
      const htmlSnapshot = newPrice instanceof HTMLElement ? newPrice.innerHTML : '';

      if (newPrice) {
        newPrice.querySelectorAll('[bss-b2b-product-price],[bss-b2b-variant-price]').forEach((node) => {
          for (const attr of [...node.attributes]) {
            if (attr.name.startsWith('bss-')) node.removeAttribute(attr.name);
          }
        });

        if (priceContainer?.isConnected) {
          priceContainer.replaceWith(newPrice);
        } else {
          const installments = this.querySelector('.installments, .tax-note');
          if (installments) installments.insertAdjacentElement('beforebegin', newPrice);
          else this.prepend(newPrice);
        }
        this.#protectPriceFromBlank(htmlSnapshot);
      }

      if (variantId != null) this.#applyFromPriceMap(variantId);
    }

    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
